/* ============================================================================
 * DASHBOARD LAYOUT CONSTANTS
 * Configuration constants and defaults for dashboard layout system
 * ============================================================================ */

import { scopedDashboardKey } from '../dashboardStorageScope';

// Cache configuration
export const DEFAULT_CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
export const DEFAULT_RETRY_ATTEMPTS = 3;
export const DEFAULT_RETRY_DELAY = 1000; // 1 second

// View modes
export const VIEW_MODES = ['client', 'admin', 'employee'] as const;
export type ViewMode = typeof VIEW_MODES[number];

// API endpoints
export const API_ENDPOINTS = {
  BASE: '/api/v1',
  LAYOUTS: '/api/v1/dashboard/layouts',
  LAYOUT_BY_MODE: (viewMode: string) => `/api/v1/dashboard/layouts/${viewMode}`,
  NAMED_LAYOUTS: (viewMode: string) => `/api/v1/dashboard/layouts/${viewMode}/named`,
  NAMED_LAYOUT_BY_ID: (viewMode: string, layoutId: string) => `/api/v1/dashboard/layouts/${viewMode}/named/${layoutId}`,
  SET_DEFAULT: (viewMode: string, layoutId: string) => `/api/v1/dashboard/layouts/${viewMode}/named/${layoutId}/default`,
} as const;

// Local storage keys
export const STORAGE_KEYS = {
  LAYOUT: (viewMode: string) => scopedDashboardKey(`dashboard-layouts-${viewMode}`),
  CURRENT_LAYOUT: (viewMode: string) => scopedDashboardKey(`dashboard-current-layout-${viewMode}`),
} as const;

// Sync status
export type SyncStatus = 'synced' | 'pending' | 'offline';

// Log messages
export const LOG_MESSAGES = {
  SAVE_SUCCESS: (key: string) => `💾 Saved layout to localStorage: ${key}`,
  SYNC_SUCCESS: (key: string) => `✅ Marked layout as synced: ${key}`,
  SYNC_PENDING: (key: string) => `⏳ Marked layout as pending sync: ${key}`,
  BACKEND_FALLBACK: '[DashboardLayoutService] Dashboard layout endpoints not implemented on backend, using local data',
  NAMED_LAYOUTS_FETCH: '📋 Named layouts from backend:',
  NAMED_LAYOUT_SAVE: (viewMode: string, name: string) => `🔄 Saving named layout: ${viewMode} - ${name}`,
  CONVERSION_START: '🔄 Converting layouts to widgets:',
  CONVERSION_SUCCESS: '✅ Converted widgets:',
  USING_BREAKPOINTS: '✅ Using breakpoints from gridConfig',
  CREATING_LAYOUTS: '📐 Creating layouts from widgets array',
  NO_WIDGETS: '⚠️ No widgets to convert',
} as const;
